// src/pages/inventory/commerce/EditCommerceProductDrawer.tsx
import React, { useState, useEffect } from "react";
import { extractValidationErrors } from "../../../utils/apiError";
import debounce from "lodash/debounce";
import { Button, Modal, Space, Tag, Avatar } from "antd";
import { SaveOutlined, DeleteOutlined, ShoppingOutlined, BoxPlotOutlined, ShopOutlined } from "@ant-design/icons";
import { createProduct, updateProduct, deleteProduct } from "../../../api/inventory/productApi";
import { fetchProductCategories } from "../../../api/inventory/productCategoryApi";
import { useNotifications } from "../../../context/NotificationContext";
import CommerceProductDetailsTab from "./components/drawerTabs/CommerceProductDetailsTab";
import CommerceStockTab from "./components/drawerTabs/CommerceStockTab";
import ShopifyDetailsTab from "./components/drawerTabs/ShopifyDetailsTab";
import { fetchClinicCompany } from "../../../api/admin/companyApi";
import VariantsManagementTab from "./components/drawerTabs/VariantsManagementTab";
import { DetailDrawer, StatusTag } from "../../../components/shared";
import type { ProductVariant, Product, ProductCategory } from '../../../types/inventory/ProductTypes';

interface EditCommerceProductDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	product: Product;
}

const EditProductDrawer: React.FC<EditCommerceProductDrawerProps> = ({ isOpen, onClose, product }) => {
	const [updatedProduct, setUpdatedProduct] = useState<Product>({ ...product });
	const [categories, setCategories] = useState<ProductCategory[]>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [activeTab, setActiveTab] = useState("details");
	const { showError, showSuccess } = useNotifications();


const [variantCount, setVariantCount] = useState<number>(product.variants?.length ?? 0);


	useEffect(() => {
		loadCategories();
	}, []);

	
	useEffect(() => {
		setUpdatedProduct({ ...product });
		setVariantCount(product.variants?.length ?? 0); // ← add this line
		if (product.product_id === 0) {
			setActiveTab("details");
		}
	}, [product]);

	const loadCategories = async () => {
		try {
			const company = await fetchClinicCompany();
			const { data } = await fetchProductCategories(1, 100, company.id, "", "commerce");
			setCategories(data.filter((cat) => cat.is_active));
		} catch (err) {
			console.error("Error loading categories:", err);
		}
	};

	const saveProduct = async () => {
		setIsSubmitting(true);
		setErrorMessages([]);
		try {
			if (!updatedProduct.name || !updatedProduct.sku) {
				setErrorMessages(["Name and SKU are required."]);
				setIsSubmitting(false);
				return;
			}

			if (product.product_id !== 0) {
				const response = await updateProduct(product.product_id, updatedProduct);
				setUpdatedProduct(response);
				showSuccess("Product updated successfully");
			} else {
				const newProduct = await createProduct(updatedProduct);
				setUpdatedProduct(newProduct);
				showSuccess("Product created successfully");
				onClose();
			}
		} catch (error: unknown) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAutoSave = debounce(() => (product.product_id ? saveProduct() : undefined), 500);

	const handleDelete = () => {
		Modal.confirm({
			title: "Delete Product",
			content: "Are you sure you want to delete this product? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteProduct(product.product_id);
					showSuccess("Product deleted successfully");
					onClose();
				} catch (error) {
					handleErrors(error);
				}
			},
		});
	};

	const handleErrors = (error: unknown) => {
		setErrorMessages(extractValidationErrors(error));
		showError("Failed to save product");
	};

	
const handleVariantsChange = (variants: ProductVariant[]) => {
	setVariantCount(variants.length);
	setUpdatedProduct((prev) => ({ ...prev, variants }));
};

	const tabItems = [
		{
			key: "details",
			label: "Product Details",
			children: <CommerceProductDetailsTab updatedProduct={updatedProduct} setUpdatedProduct={setUpdatedProduct} handleAutoSave={handleAutoSave} categories={categories} />,
		},
		{
			key: "variants",
			label: (
				<Space size={4}>
					<BoxPlotOutlined />
					Variants
					{variantCount > 0 && <Tag style={{ background: "var(--mg-primary-bg)", borderColor: "var(--mg-primary-border)", color: "var(--mg-primary)" }}>{variantCount}</Tag>}
				</Space>
			),
			children: <VariantsManagementTab product={updatedProduct} onVariantUpdate={handleVariantsChange} />,
			disabled: !product.product_id,
		},
		{
			key: "stock",
			label: "Inventory & Stock",
			children: <CommerceStockTab product={updatedProduct} />,
			disabled: !product.product_id,
		},
		{
			key: "shopify",
			label: (
				<Space size={4}>
					<ShoppingOutlined />
					Shopify
					{updatedProduct.product_shopify_id && (
						<Tag style={{ background: "var(--mg-success-bg)", borderColor: "var(--mg-success)", color: "var(--mg-success)" }}>Connected</Tag>
					)}
				</Space>
			),
			children: <ShopifyDetailsTab product={updatedProduct} setProduct={setUpdatedProduct} />,
			disabled: !product.product_id,
		},
	].filter((item) => !item.disabled);

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			avatar={
				<Avatar
					icon={<ShopOutlined />}
					style={{ background: "var(--mg-primary-bg)", color: "var(--mg-primary)" }}
					size={44}
				/>
			}
			title={product.product_id ? updatedProduct.name : "Create New Product"}
			subtitle={product.product_id ? `SKU: ${updatedProduct.sku || "—"}` : undefined}
			titleMeta={updatedProduct.status ? <StatusTag status={updatedProduct.status} /> : undefined}
			headerActions={
				<Space>
					<Button type="primary" icon={<SaveOutlined />} onClick={saveProduct} loading={isSubmitting}>
						Save
					</Button>
					{product.product_id !== 0 && (
						<Button danger icon={<DeleteOutlined />} onClick={handleDelete} disabled={isSubmitting}>
							Delete
						</Button>
					)}
				</Space>
			}
			errorMessages={errorMessages}
			onClearErrors={() => setErrorMessages([])}
			tabs={tabItems}
			activeTab={activeTab}
			onTabChange={setActiveTab}
		/>
	);
};

export default EditProductDrawer;